/**
 * % By State Distribution — read-only table.
 *
 * The rows are no longer user-editable. They get populated client-side
 * by `useScheduleAggregates` (pure functions in `domain/pricing`) on
 * Relativity / Facility Schedule changes.
 *
 * Columns: Venue / State · Exposure · % Overall · Rates. The
 * % Overall column has a fixed width so the progress bar always
 * renders at a comfortable reading size.
 */
import { useFormContext, useWatch } from "react-hook-form";
import { Globe05 } from "@untitledui/icons";
import { Table } from "@tricura/atlas-ui";
import { FormGroup } from "../../../../components/form-group";
import { TableEmptyState } from "../../../../components/table-empty-state";
import { allocateWholePercents } from "../../../../domain/pricing";
import type { ScheduleFormData } from "../schema";

// Percentages — the table stretches to the FormGroup's full width so
// the trailing space after "Rates" disappears. Proportions roughly
// match the previous pixel widths (240 / 200 / 320 / 160 → 25 / 20 / 35 / 20).
const W = {
    state: "25%",
    exposure: "20%",
    overall: "35%",
    rates: "20%",
} as const;

const fixed = (pct: string) => ({ width: pct });

const READONLY_CELL = "text-secondary text-sm tabular-nums";

const TOOLTIP = {
    state:
        "Each unique state where a facility in your schedule operates.\n\n" +
        "This table populates automatically from the Facility Schedule. " +
        "Add or edit facilities above to update these values.",
    exposure:
        "Total weighted equivalency for all facilities in this state, combining occupied beds " +
        "across Skilled, Assisted, Independent, and Additional Exposure categories.\n\n" +
        "This table populates automatically from the Facility Schedule. " +
        "Add or edit facilities above to update these values.",
    overall:
        "This state's share of the total portfolio exposure across all states.\n\n" +
        "Calculated as state exposure ÷ total exposure for all states.\n\n" +
        "This table populates automatically from the Facility Schedule.",
    rates:
        "Average rate across all facilities in this state.\n\n" +
        "This table populates automatically from the Facility Schedule.",
};

function num(v: string | undefined): number {
    if (!v) return 0;
    const n = Number(String(v).replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
}

/** One decimal, mirroring the Facility Schedule's Equivalency column
 *  (exposure IS the per-state equivalency sum — OR-1027). */
function displayExposure(n: number): string {
    if (!Number.isFinite(n) || n === 0) return "—";
    return n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function displayRate(v: string | undefined): string {
    if (!v) return "—";
    const n = num(v);
    if (!Number.isFinite(n) || n === 0) return "—";
    return n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 4 });
}

export function StateDistributionSection() {
    const { control } = useFormContext<ScheduleFormData>();
    const rows = (useWatch({ control, name: "stateDistribution" }) || []) as ScheduleFormData["stateDistribution"];

    const exposures = rows.map((row) => num(row?.exposure));
    const total = exposures.reduce((sum, v) => sum + v, 0);
    // Whole percents that always add up to 100 (largest remainder) —
    // rounding each share on its own drifted the total to 99 (OR-1027).
    const percents = allocateWholePercents(exposures);

    return (
        <FormGroup title="% By State Distribution" flushBody>
            <div className="relative overflow-hidden">
            <Table
                aria-label="State distribution"
                wrapperClassName="min-h-96"
                className="w-full [&_tbody_tr:last-child]:border-b-0!"
            >
                <Table.Header>
                    <Table.Head id="state" label="Venue / State" isRowHeader className="pl-9" style={fixed(W.state)} tooltip={TOOLTIP.state} />
                    <Table.Head id="exposure" label="Exposure" className="pl-9" style={fixed(W.exposure)} tooltip={TOOLTIP.exposure} />
                    <Table.Head id="overall" label="% Overall" className="pl-9" style={fixed(W.overall)} tooltip={TOOLTIP.overall} />
                    <Table.Head id="rates" label="Rates" className="pl-9" style={fixed(W.rates)} tooltip={TOOLTIP.rates} />
                </Table.Header>
                <Table.Body>
                    {rows.map((row, idx) => {
                        const exposureNum = exposures[idx];
                        const pct = percents[idx];
                        return (
                            <Table.Row id={row?.id ?? `state-dist-${idx}`} key={row?.id ?? idx}>
                                <Table.Cell style={fixed(W.state)}>
                                    <span className="text-primary text-sm font-medium" data-autotest="schedule-state-dist-state">
                                        {row?.state || "—"}
                                    </span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.exposure)}>
                                    <span className={READONLY_CELL} data-autotest="schedule-state-dist-exposure">
                                        {displayExposure(exposureNum)}
                                    </span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.overall)}>
                                    <div className="flex items-center gap-3">
                                        <div className="bg-quaternary h-2 flex-1 overflow-hidden rounded-full">
                                            <div className="bg-brand-solid h-full" style={{ width: `${pct}%` }} />
                                        </div>
                                        <span className="text-tertiary w-10 text-right text-sm font-medium tabular-nums">{pct}%</span>
                                    </div>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.rates)}>
                                    <span className={READONLY_CELL} data-autotest="schedule-state-dist-rates">
                                        {displayRate(row?.rates)}
                                    </span>
                                </Table.Cell>
                            </Table.Row>
                        );
                    })}
                    {rows.length > 0 && (
                        <Table.Row id="__totals__" className="bg-secondary font-semibold">
                            <Table.Cell className="text-quaternary pl-9 text-xs uppercase tracking-wider" style={fixed(W.state)}>
                                Total
                            </Table.Cell>
                            <Table.Cell className="pl-9" style={fixed(W.exposure)}>
                                <span className="text-primary text-sm tabular-nums" data-autotest={`schedule-state-dist-totals-exposure`}>
                                    {displayExposure(total)}
                                </span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.overall)} />
                            <Table.Cell style={fixed(W.rates)} />
                        </Table.Row>
                    )}
                </Table.Body>
            </Table>
            {rows.length === 0 && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="pointer-events-auto">
                        <TableEmptyState
                            icon={Globe05}
                            title="No state distribution yet"
                            description="Once facilities are added above, this table fills in automatically."
                        />
                    </div>
                </div>
            )}
            </div>
        </FormGroup>
    );
}
